import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Star, Loader2, MessageSquare, Check } from "lucide-react";
import { toast } from "sonner";
import Brand from "@/components/Brand";

// Feedback form — reachable from Login, Home, Plans, and Help.
// Works both anonymously (from Login) and while signed in (pre-fills identity).
// `?from=<context>` query param stamps the record for admin filtering.

export default function Feedback() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const context = new URLSearchParams(location.search).get("from") || "other";

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) {
      toast.error("Please write a longer message.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        rating: rating || null,
        name: user ? undefined : name.trim() || undefined,
        email: user ? undefined : email.trim() || undefined,
        context,
      });
      setDone(true);
      toast.success("Thanks — your feedback was recorded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send feedback.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 text-center bg-background"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="feedback-done"
      >
        <div className="h-14 w-14 border border-accent rounded-full flex items-center justify-center text-accent">
          <Check size={28} strokeWidth={1.5} />
        </div>
        <h1 className="editorial-h2 mt-6">Thank you.</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm">
          Your feedback helps us make StyleBay better. We read every submission.
        </p>
        <Button
          onClick={() => navigate(-1)}
          className="mt-8 rounded-none h-11 px-8 uppercase tracking-[0.2em] text-xs"
          data-testid="feedback-return-btn"
        >
          Back
        </Button>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="feedback-page"
    >
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            data-testid="feedback-back-btn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em]"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <Brand size="sm" />
          <span className="w-16" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10 lg:py-16">
        <p className="eyebrow flex items-center gap-2">
          <MessageSquare size={12} /> Feedback
        </p>
        <h1 className="editorial-h1 mt-3">How can we do better?</h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-md">
          Bugs, feature requests, delights, complaints — all welcome. We read every message.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="feedback-form">
          {/* Rating */}
          <div>
            <Label className="eyebrow">Rate your experience</Label>
            <div className="flex gap-2 mt-3" data-testid="feedback-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  data-testid={`feedback-rating-${n}`}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <Star
                    size={28}
                    strokeWidth={1.5}
                    className={
                      (hoverRating || rating) >= n
                        ? "fill-accent text-accent"
                        : "text-muted-foreground"
                    }
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="fb-msg" className="eyebrow">Your message</Label>
            <Textarea
              id="fb-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={7}
              maxLength={4000}
              placeholder="Tell us what you love, what's broken, or what you wish StyleBay could do…"
              className="rounded-none mt-2"
              data-testid="feedback-message-input"
              required
            />
            <p className="text-[10px] text-muted-foreground mt-1.5">{message.length} / 4000</p>
          </div>

          {/* Identity — hidden if signed in, shown for anonymous form */}
          {!user && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fb-name" className="eyebrow">Name (optional)</Label>
                <Input
                  id="fb-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-none mt-2 h-11"
                  data-testid="feedback-name-input"
                  placeholder="Aria Verma"
                />
              </div>
              <div>
                <Label htmlFor="fb-email" className="eyebrow">Email (optional)</Label>
                <Input
                  id="fb-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-none mt-2 h-11"
                  data-testid="feedback-email-input"
                  placeholder="you@stylebay.app"
                />
              </div>
            </div>
          )}

          {user && (
            <p className="text-xs text-muted-foreground border border-border p-3">
              Sending as <b>{user.name || user.email}</b> ({user.email}).
            </p>
          )}

          <Button
            type="submit"
            disabled={busy || message.trim().length < 3}
            data-testid="feedback-submit-btn"
            className="w-full h-12 rounded-none uppercase tracking-[0.22em] text-xs"
          >
            {busy ? (
              <><Loader2 size={14} className="animate-spin mr-2" /> Sending</>
            ) : (
              "Send feedback"
            )}
          </Button>

          <p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em] text-center">
            By sending, you agree to our{" "}
            <Link to="/privacy" className="underline">privacy policy</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
